import { computed, inject } from '@angular/core';
import { signalStoreFeature, withComputed, withState } from '@ngrx/signals';
import { map } from 'rxjs';
import { initialState } from './application.store';
import { BreakpointObserver } from '@angular/cdk/layout';
import { getNameInitials } from '@shared/utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { createHasServiceComputedSignals } from './auth.feature';

// eslint-disable-next-line
export const withComputedApplication = () =>
  signalStoreFeature(
    withState(initialState),
    withComputed((store, responsive = inject(BreakpointObserver)) => {
      const profile = store.userProfile;

      const language = computed(() => profile()?.info?.language);

      const userFirstName = computed(() => profile()?.info?.firstName);

      const userInitials = computed(() =>
        getNameInitials(profile()?.info?.firstName, profile()?.info?.lastName)
      );

      const selectedAccount = computed(() => store.selectedAccount());

      const isSelectedAccountUnconfirmed = computed(
        () => !!selectedAccount()?.unconfirmedCriticalInfo
      );

      const xsWidth = '(max-width: 599.98px)';
      const portrait = '(orientation: portrait)';
      const isMobile = toSignal(
        responsive
          .observe([xsWidth, portrait])
          .pipe(
            map(
              state => state.breakpoints[xsWidth] && state.breakpoints[portrait]
            )
          ),
        { initialValue: false, rejectErrors: true }
      );

      const smWidth = '(max-width: 959.98px)';
      const landscape = '(orientation: landscape)';
      const isMobileLandscape = toSignal(
        responsive
          .observe([smWidth, landscape])
          .pipe(
            map(
              state => state.breakpoints[xsWidth] && state.breakpoints[portrait]
            )
          ),
        { initialValue: false }
      );

      const xxxlWidth = '(min-width: 1900px)';
      const is2xl = toSignal(
        responsive
          .observe([xxxlWidth])
          .pipe(map(state => state.breakpoints[xxxlWidth])),
        { initialValue: false, rejectErrors: true }
      );

      const hasServiceSignals = createHasServiceComputedSignals(store);
      const hasDispatcherService = hasServiceSignals.hasDispatcherService;

      const companyInfo = computed(() => selectedAccount()?.info?.companyInfo);

      const isDispatcherInfoIncomplete = computed(
        () => isRegistrationNumberIncomplete() || isCreationDateIncomplete()
      );

      const isRegistrationNumberIncomplete = computed(
        () => hasDispatcherService() && !companyInfo()?.registrationNumber
      );

      const isCreationDateIncomplete = computed(
        () => hasDispatcherService() && !companyInfo()?.creationDate
      );

      const isDispatcherDocsIncomplete = computed(
        () => isBusinessLicenseIncomplete() || isPublicLiabilityIncomplete()
      );

      const isBusinessLicenseIncomplete = computed(
        () => hasDispatcherService() && !companyInfo()?.businessLicenseDocument
      );

      const isPublicLiabilityIncomplete = computed(
        () =>
          hasDispatcherService() &&
          !companyInfo()?.publicLiabilityPolicyDocument &&
          companyInfo()?.hasLiabilityInsurance
      );

      const isDriverPermissionsIncomplete = computed(
        () =>
          hasDispatcherService() &&
          selectedAccount()?.drivePermissions?.length === 0
      );

      const isDispatcherDetailsIncomplete = computed(
        () => isDispatcherInfoIncomplete() || isDispatcherDocsIncomplete()
      );

      const isDispatcherAccountIncomplete = computed(
        () => isDispatcherDetailsIncomplete() || isDriverPermissionsIncomplete()
      );

      return {
        // Application
        language,
        userFirstName,
        userInitials,
        isSelectedAccountUnconfirmed,
        isMobile,
        isMobileLandscape,
        is2xl,

        // Account Information Status
        isDispatcherInfoIncomplete,
        isDispatcherDocsIncomplete,
        isCreationDateIncomplete,
        isRegistrationNumberIncomplete,
        isBusinessLicenseIncomplete,
        isPublicLiabilityIncomplete,
        isDriverPermissionsIncomplete,
        isDispatcherDetailsIncomplete,
        isDispatcherAccountIncomplete
      };
    })
  );
